import { i18n } from "../i18n/i18n";

import React, { Component, createRef, RefObject } from 'react'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import { forEach, map } from 'lodash'
import routes from '../routes/routes'
import csvApi, { CsvParseRequest } from '../apis/csv.api'
import purchaseOrderApi, {
  PurchaseOrderImportRequest, PurchaseOrderImportRequestPurchaseOrder
} from '../apis/purchaseOrder.api'
import Button from '../components/Button'
import Card from '../components/Card'
import ContentLayout from '../components/ContentLayout'
import ContentMain from '../components/ContentMain'
import InputFile from '../components/InputFile'
import TemplateDownloader from '../components/TemplateDownloader'
import PageHeading from '../components/PageHeading'
import TableHorizontal, {
  TableHorizontalData,
  TableHorizontalHeader
} from '../components/TableHorizontal'
import CardButton from '../components/CardButton'

interface PurchaseOrdersImportCsvData {
  id: string
  purchaseOrder: string
  vendor: string
  vendorSite: string
  item: string
  line: string
  lineQuantity: string
  deliveryDate: string
}

type PurchaseOrdersImportTableData = TableHorizontalData<PurchaseOrdersImportCsvData>

interface PurchaseOrdersImportProps extends RouteComponentProps {}

interface PurchaseOrdersImportState {
  file: File | null
  filename: string
  purchaseOrders: PurchaseOrdersImportTableData[]
}

class PurchaseOrdersImport
  extends Component<PurchaseOrdersImportProps, PurchaseOrdersImportState> {
  fileRef: RefObject<HTMLInputElement>

  tableHeaders: TableHorizontalHeader[] = [
    { heading: i18n("Purchase order"), path: 'purchaseOrder' },
    { heading: i18n("Line"), path: 'line' },
    { heading: i18n("Vendor"), path: 'vendor' },
    { heading: i18n("Vendor site"), path: 'vendorSite' },
    { heading: i18n("Item"), path: 'item' },
    { heading: i18n("Line quantity"), path: 'lineQuantity' },
    { heading: i18n("Delivery date"), path: 'deliveryDate', date: true }
  ]

  constructor (props: PurchaseOrdersImportProps) {
    super(props)
    this.state = {
      file: null,
      filename: '',
      purchaseOrders: []
    }
    this.fileRef = createRef()
    this.handleFileChange = this.handleFileChange.bind(this)
    this.handleCancelClick = this.handleCancelClick.bind(this)
    this.handleImportClick = this.handleImportClick.bind(this)
  }

  get purchaseOrders ():PurchaseOrderImportRequestPurchaseOrder[] {
    return map(this.state.purchaseOrders, purchaseOrderData => {
      const { purchaseOrder, line, vendor, vendorSite } = purchaseOrderData
      const { item, lineQuantity: quantity } = purchaseOrderData
      const { deliveryDate: deliveryAt } = purchaseOrderData
      return {
        purchaseOrder, line, vendor, vendorSite, item, quantity, deliveryAt
      }
    })
  }

  import () {
    const { purchaseOrders } = this
    const payload: PurchaseOrderImportRequest = {}
    if (purchaseOrders) payload.purchaseOrders = purchaseOrders
    purchaseOrderApi.import(payload, purchaseOrders => {
      if (!purchaseOrders) return
      this.props.history.push(routes.purchaseOrders)
    })
  }

  handleFileChange (name: string, file: File | null, filename: string) {
    const payload: CsvParseRequest = {}
    if (file) payload.csv = file
    this.setState({ file, filename })
    csvApi.parse<PurchaseOrdersImportTableData[]>(payload, purchaseOrders => {
      if (!purchaseOrders) return
      purchaseOrders = forEach(purchaseOrders, purchaseOrder => {
        purchaseOrder.id = purchaseOrder.purchaseOrder as never
      })
      this.setState({ purchaseOrders })
    })
  }

  handleCancelClick () {
    const fileInput = this.fileRef.current
    if (fileInput) fileInput.value = ''
    this.setState({ filename: '', purchaseOrders: [] })
  }

  handleImportClick () {
    this.import()
  }

  render () {
    return (
      <ContentLayout>
        <ContentMain layout="full">
          <Card>
            <PageHeading
              text={i18n("Import Purchase Orders")}
              doc='purchase-orders-import-purchase-orders' />
            <div className="grid lg:grid-cols-3 mb-4">
              <div className="col-span-2">
                <div className="flex flex-auto flex-col md:flex-row md:items-end gap-4">
                  <div className="flex-1">
                    <InputFile
                      className="mb-0"
                      name="purchaseOrders"
                      filename={this.state.filename}
                      accept=".csv"
                      label="Purchase orders"
                      placeholder="Choose purchase orders"
                      reference={this.fileRef}
                      onChange={this.handleFileChange} />
                  </div>
                  <div className="flex gap-4">
                    <Button
                      text={i18n("Cancel")}
                      type="secondary"
                      onClick={this.handleCancelClick} />
                    <Button
                      text={i18n("Import")}
                      onClick={this.handleImportClick} />
                  </div>
                </div>
              </div>
            </div>
            <TemplateDownloader
              text="Download sample purchase orders template"
              filename="purchaseOrders" />
            <h3 className="font-medium text-sm mb-4">Purchase Orders</h3>
            <TableHorizontal
              headers={this.tableHeaders}
              data={this.state.purchaseOrders} />
            <CardButton cancelText={i18n("Cancel")} />
          </Card>
        </ContentMain>
      </ContentLayout>
    )
  }
}

export default withRouter(PurchaseOrdersImport)
